import { Ref, useEffect, useEffectEvent, useState } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  ItemNoAndTypeWithPieces,
  ItemNoAndTypeWithOrderedDetails,
} from "@coworker/apprestructured/src/shared/types/item";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  isLoading,
  Loading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import LoadingCreatorFragment from "../InvalidCreatorFragment/InvalidCreatorFragment";
import { ListItem } from "../../../SelectListItem/SelectListItem";
import { ArticlesQuantity } from "../../../ArticlesQuantity/ArticlesQuantity";

interface VisibilityCondition {
  field: string;
  value: any;
  operator: "in";
}

export interface ArticlesQuantityCreatorFragmentConfigProps {
  title: string;
  field: string;
  visibility: VisibilityCondition;
}

interface ArticlesQuantityCreatorFragmentProps {
  configProps: ArticlesQuantityCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
}

function shouldShowField(
  task: CreatorFragmentCommonProps["task"],
  visibility: VisibilityCondition
): boolean {
  const currentValue = getByPath(task, visibility.field) as ListItem | null;

  if (visibility.operator === "in") {
    return Array.isArray(visibility.value)
      ? visibility.value.includes(currentValue?.value)
      : false;
  }

  return true;
}

const ArticlesQuantityCreatorFragment = ({
  configProps,
  commonProps,
}: ArticlesQuantityCreatorFragmentProps) => {
  const { field, visibility } = configProps;
  const { task, taskType, onTaskChange } = commonProps;

  const itemNosAndTypeWithPieces = validateInitialDetailsFieldValue<
    ItemNoAndTypeWithPieces[]
  >(getByPath(task, field), field, taskType);

  const initializeItemsOrderedDetails = useEffectEvent(() => {
    if (
      !Array.isArray(itemNosAndTypeWithPieces) ||
      isLoading(itemNosAndTypeWithPieces)
    ) {
      return;
    }

    const hasOrderedFields = itemNosAndTypeWithPieces.some(
      (item) => "piecesOrdered" in item || "pTagsOrdered" in item
    );

    if (!hasOrderedFields) {
      const itemsWithOrderedDetails = itemNosAndTypeWithPieces.map((item) => ({
        ...item,
        piecesOrdered: item.pieces,
        pTagsOrdered: item.pieces,
      }));

      onTaskChange({ details: { items: itemsWithOrderedDetails } });
    }
  });

  useEffect(() => {
    initializeItemsOrderedDetails();
  }, []);

  const initialItemsOrderedDetails: ItemNoAndTypeWithOrderedDetails[] | null =
    Array.isArray(itemNosAndTypeWithPieces)
      ? itemNosAndTypeWithPieces.map((item) => ({
          ...item,
          piecesOrdered: item.pieces,
          pTagsOrdered: item.pieces,
        }))
      : null;

  const [itemsOrderedDetails, setItemsOrderedDetails] = useState<
    ItemNoAndTypeWithOrderedDetails[] | null | Loading
  >(initialItemsOrderedDetails);

  const handlePiecesChange = (itemNo: string, newPieces: number) => {
    setItemsOrderedDetails((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((item) =>
        item.no === itemNo ? { ...item, piecesOrdered: newPieces } : item
      );
    });
  };

  const handleConfirm = () => {
    if (Array.isArray(itemsOrderedDetails)) {
      onTaskChange({ details: { items: itemsOrderedDetails } });
    }
  };

  const { items, isFetchingItems } = useAllAttachedItems(
    itemNosAndTypeWithPieces ?? LOADING
  );

  if (!shouldShowField(task, visibility)) {
    return null;
  }

  if (isFetchingItems || isLoading(itemNosAndTypeWithPieces)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <ArticlesQuantity
      itemsNoAndTypeOrderedDetails={itemsOrderedDetails}
      items={items}
      handlePiecesChange={handlePiecesChange}
      handleConfirm={handleConfirm}
    />
  );
};

export default ArticlesQuantityCreatorFragment;
